import React, { memo } from 'react';
import { 
  Inbox, 
  Send, 
  Star, 
  Trash2, 
  Archive, 
  Mail, 
  Edit3, 
  Reply, 
  Forward, 
  MoreHorizontal,
  Paperclip,
  Flag
} from 'lucide-react';

const MailContent = memo(({ activity }) => {
  const isScrolling = activity?.action === 'scroll';
  const isClicking = activity?.action === 'click';
  const isSelecting = activity?.action === 'select';
  const isTyping = activity?.action === 'type';
  
  // Track which email action we're on
  const [actionCount, setActionCount] = React.useState(0);
  React.useEffect(() => {
    if (activity) {
      setActionCount(prev => prev + 1);
    }
  }, [activity]);
  
  const showCompose = actionCount > 4 && isTyping;
  
  const emails = [
    {
      id: 1,
      sender: 'Sarah Chen',
      avatar: '👩‍💼',
      subject: 'Q4 Product Roadmap Review',
      preview: 'Hi team, I wanted to share the updated roadmap for Q4. Please review before...',
      time: '9:42 AM',
      unread: true,
      starred: true,
      hasAttachment: true
    },
    {
      id: 2,
      sender: 'GitHub',
      avatar: '🐙',
      subject: '[repo-name] Pull Request #247 merged',
      preview: 'Your pull request "Add authentication middleware" has been successfully merged...',
      time: '8:15 AM',
      unread: true,
      starred: false,
      hasAttachment: false
    },
    {
      id: 3,
      sender: 'Alex Rodriguez',
      avatar: '👨‍💻',
      subject: 'Re: Design System Updates',
      preview: 'Thanks for the update! The new components look great. I have a few suggestions...',
      time: 'Yesterday',
      unread: false,
      starred: false,
      hasAttachment: true
    },
    {
      id: 4,
      sender: 'LinkedIn',
      avatar: '💼',
      subject: 'You appeared in 12 searches this week',
      preview: 'Your profile is getting more visibility. Here are some connection suggestions...',
      time: 'Yesterday',
      unread: false,
      starred: false,
      hasAttachment: false
    },
    {
      id: 5,
      sender: 'Maya Patel',
      avatar: '👩‍🔬',
      subject: 'Meeting Notes - Sprint Planning',
      preview: 'Attached are the notes from today\'s sprint planning session. Key action items...',
      time: 'Dec 2',
      unread: false,
      starred: true,
      hasAttachment: true
    }
  ];

  return (
    <div className="flux-mail-content">
      {/* Compose Window */}
      {showCompose && (
        <div className="compose-window">
          <div className="compose-header">
            <span className="compose-title">New Message</span>
            <button className="compose-close">×</button>
          </div>
          <div className="compose-body">
            <div className="compose-field">
              <span className="field-label">To:</span>
              <input className="field-input" value="team@fluxspaces.com" readOnly />
            </div>
            <div className="compose-field">
              <span className="field-label">Subject:</span>
              <input className="field-input" value="Weekly Update - Jan 2024" readOnly />
            </div>
            <div className="compose-message">
              <p>Hi everyone,</p>
              <p>Quick update on this week's progress:</p>
              <p>- Completed the new dashboard design</p>
              <p>- Fixed critical bugs in production</p>
              <p>- Started work on mobile optimization{isTyping && <span className="typing-cursor">|</span>}</p>
            </div>
          </div>
          <div className="compose-footer">
            <button className="compose-send">Send</button>
          </div>
        </div>
      )}
    
      {/* Mail Toolbar */}
      <div className="mail-toolbar">
        <button className={`mail-btn primary ${showCompose ? 'active' : ''}`}>
          <Edit3 size={10} />
          <span>Compose</span>
        </button>
        <div className="toolbar-divider"></div>
        <button className="mail-btn" title="Reply">
          <Reply size={8} />
        </button>
        <button className="mail-btn" title="Forward">
          <Forward size={8} />
        </button>
        <button className="mail-btn" title="Archive">
          <Archive size={8} />
        </button>
        <button className="mail-btn" title="Delete">
          <Trash2 size={8} />
        </button>
        <button className="mail-btn" title="More">
          <MoreHorizontal size={8} />
        </button>
      </div>

      <div className="mail-layout">
        {/* Sidebar */}
        <div className="mail-sidebar">
          <div className="sidebar-folder active">
            <Inbox size={8} />
            <span className="folder-name">Inbox</span>
            <span className="folder-count">12</span>
          </div>
          <div className="sidebar-folder">
            <Star size={8} />
            <span className="folder-name">Starred</span>
            <span className="folder-count">3</span>
          </div>
          <div className="sidebar-folder">
            <Send size={8} />
            <span className="folder-name">Sent</span>
          </div>
          <div className="sidebar-folder">
            <Archive size={8} />
            <span className="folder-name">Archive</span>
          </div>
          <div className="sidebar-folder">
            <Trash2 size={8} />
            <span className="folder-name">Trash</span>
          </div>
        </div>

        {/* Email List */}
        <div className={`mail-list ${isScrolling ? 'scrolling' : ''}`}>
          {emails.map((email, index) => (
            <div 
              key={email.id}
              className={`mail-item ${email.unread ? 'unread' : ''} ${
                index === 0 && isClicking ? 'clicking' : ''
              } ${index === 1 && isSelecting ? 'selecting' : ''} ${index === 2 ? 'selected' : ''}`}
            >
              <div className="mail-item-avatar">{email.avatar}</div>
              <div className="mail-item-content">
                <div className="mail-item-header">
                  <span className="mail-sender">{email.sender}</span>
                  <span className="mail-time">{email.time}</span>
                </div>
                <div className="mail-subject">
                  {email.hasAttachment && <Paperclip size={6} className="attachment-icon" />}
                  {email.starred && <Star size={6} className="star-icon filled" />}
                  {email.subject}
                </div>
                <div className="mail-preview">{email.preview}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Email Preview Pane */}
        <div className="mail-preview">
          <div className="preview-header">
            <div className="preview-subject">
              <h3>Q4 Product Roadmap Review</h3>
              <div className="preview-actions">
                <button className="preview-btn" title="Star">
                  <Star size={8} className="filled" />
                </button>
                <button className="preview-btn" title="Flag">
                  <Flag size={8} />
                </button>
              </div>
            </div>
            <div className="preview-meta">
              <div className="preview-sender">
                <span className="sender-avatar">👩‍💼</span>
                <div className="sender-info">
                  <span className="sender-name">Sarah Chen</span>
                  <span className="sender-email">sarah.chen@company.com</span>
                </div>
              </div>
              <span className="preview-time">Today at 9:42 AM</span>
            </div>
          </div>

          <div className="preview-body">
            <p className="preview-text">Hi team,</p>
            <p className="preview-text">
              I wanted to share the updated product roadmap for Q4. We've made some significant 
              changes based on customer feedback and market research.
            </p>
            <p className="preview-text">
              <strong>Key highlights:</strong>
            </p>
            <ul className="preview-list">
              <li>New analytics dashboard (Priority: High)</li>
              <li>Mobile app optimization</li>
              <li>API v2 migration</li>
              <li>Enhanced security features</li>
            </ul>
            <p className="preview-text">
              Please review the attached document before our meeting on Thursday. Let me know 
              if you have any questions or concerns.
            </p>
            <p className="preview-text signature">
              Best regards,<br />
              Sarah
            </p>

            <div className="preview-attachment">
              <Paperclip size={8} />
              <div className="attachment-info">
                <span className="attachment-name">Q4-Roadmap-2024.pdf</span>
                <span className="attachment-size">2.4 MB</span>
              </div>
            </div>

            {/* Reply Box when typing */}
            {isTyping && (
              <div className="reply-box typing">
                <div className="reply-header">
                  <Reply size={8} />
                  <span>Reply to Sarah Chen</span>
                </div>
                <div className="reply-input">
                  <p className="typing-text">Thanks for sharing! I'll review the document and get back to you with feedback{isTyping && <span className="typing-cursor">|</span>}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

MailContent.displayName = 'MailContent';

export default MailContent;
